/**
 * Error Log Component
 * -------------------
 * Displays a detailed log of errors made during an exercise
 * Shows expected vs played notes and timing information
 */

import { motion } from "framer-motion";
import { X, XCircle, Clock, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TimingResult } from "@/hooks/useMetronome";

export interface ErrorLogEntry {
  noteIndex: number;
  expectedNote: string;
  playedNote: string;
  expectedMidi: number;
  playedMidi: number;
  timing?: TimingResult;
  measure: number;
  beatInMeasure: number;
}

interface ErrorLogDialogProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ErrorLogEntry[];
  totalNotes: number;
  accuracy: number;
}

export default function ErrorLogDialog({
  isOpen,
  onClose,
  errors,
  totalNotes,
  accuracy,
}: ErrorLogDialogProps) {
  // Early return keeps this dialog fully unmounted when closed.
  if (!isOpen) return null;

  // Derived summary values shown in the header stats row.
  const correctNotes = totalNotes - errors.length;
  const pitchErrors = errors.filter(e => e.expectedMidi !== e.playedMidi).length;
  const timingErrors = errors.filter(e => e.expectedMidi === e.playedMidi).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "oklch(0 0 0 / 70%)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="w-full max-w-2xl max-h-[80vh] rounded-lg overflow-hidden flex flex-col"
        style={{
          background: "oklch(0.17 0.015 270)",
          border: "1px solid oklch(0.28 0.010 270)",
          boxShadow: "0 12px 48px oklch(0 0 0 / 50%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b shrink-0"
          style={{ borderColor: "oklch(0.28 0.010 270)", background: "oklch(0.19 0.015 270)" }}
        >
          <div>
            <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: "oklch(0.90 0.01 270)" }}>
              <Music size={18} />
              Exercise Error Log
            </h3>
            <p className="text-xs mt-0.5" style={{ color: "oklch(0.50 0.01 270)" }}>
              Review your mistakes to improve
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: "oklch(0.22 0.012 270)", color: "oklch(0.60 0.01 270)" }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Summary */}
        <div
          className="px-5 py-4 border-b shrink-0"
          style={{
            borderColor: "oklch(0.28 0.010 270)",
            background: "oklch(0.18 0.015 270)",
          }}
        >
          <div className="grid grid-cols-4 gap-4">
            <SummaryStat
              label="Accuracy"
              value={`${accuracy}%`}
              color={accuracy >= 90 ? "oklch(0.70 0.18 150)" : accuracy >= 75 ? "oklch(0.82 0.17 75)" : "oklch(0.65 0.24 25)"}
            />
            <SummaryStat
              label="Correct Notes"
              value={correctNotes}
              color="oklch(0.70 0.18 150)"
            />
            <SummaryStat
              label="Pitch Errors"
              value={pitchErrors}
              color="oklch(0.65 0.24 25)"
            />
            <SummaryStat
              label="Timing Errors"
              value={timingErrors}
              color="oklch(0.75 0.18 30)"
            />
          </div>
        </div>

        {/* Error List */}
        <div className="flex-1 overflow-y-auto p-5">
          {errors.length === 0 ? (
            <div className="text-center py-12">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{
                  background: "oklch(0.70 0.18 150 / 15%)",
                  border: "2px solid oklch(0.70 0.18 150 / 30%)",
                }}
              >
                <Music size={28} style={{ color: "oklch(0.70 0.18 150)" }} />
              </div>
              <h4 className="text-base font-bold mb-2" style={{ color: "oklch(0.82 0.17 75)" }}>
                Perfect Performance!
              </h4>
              <p className="text-sm" style={{ color: "oklch(0.50 0.01 270)" }}>
                You played all notes correctly. Excellent work!
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {errors.map((error, index) => (
                <ErrorEntry key={index} error={error} index={index} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-4 border-t shrink-0 flex justify-end"
          style={{ borderColor: "oklch(0.28 0.010 270)", background: "oklch(0.19 0.015 270)" }}
        >
          <Button onClick={onClose}>Close</Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SummaryStat({ label, value, color }: { label: string; value: string | number; color: string }) {
  // Small reusable metric tile used in the summary grid.
  return (
    <div className="text-center">
      <div className="text-2xl font-bold font-mono" style={{ color }}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide font-semibold mt-0.5" style={{ color: "oklch(0.45 0.01 270)" }}>
        {label}
      </div>
    </div>
  );
}

function ErrorEntry({ error, index }: { error: ErrorLogEntry; index: number }) {
  // Determine whether the entry was primarily a pitch issue or timing issue.
  const isPitchError = error.expectedMidi !== error.playedMidi;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="rounded-lg p-3 border"
      style={{
        background: "oklch(0.19 0.015 270)",
        borderColor: isPitchError ? "oklch(0.65 0.24 25 / 30%)" : "oklch(0.75 0.18 30 / 30%)",
      }}
    >
      <div className="flex items-start gap-3">
        {/* Error Icon */}
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
          style={{
            background: isPitchError ? "oklch(0.65 0.24 25 / 15%)" : "oklch(0.75 0.18 30 / 15%)",
            border: `1px solid ${isPitchError ? "oklch(0.65 0.24 25 / 30%)" : "oklch(0.75 0.18 30 / 30%)"}`,
          }}
        >
          {isPitchError ? (
            <XCircle size={16} style={{ color: "oklch(0.65 0.24 25)" }} />
          ) : (
            <Clock size={16} style={{ color: "oklch(0.75 0.18 30)" }} />
          )}
        </div>

        {/* Error Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-mono font-bold" style={{ color: "oklch(0.70 0.01 270)" }}>
              Note #{error.noteIndex + 1} — M{error.measure + 1}, Beat {error.beatInMeasure + 1}
            </span>
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded"
              style={{
                background: isPitchError ? "oklch(0.65 0.24 25 / 20%)" : "oklch(0.75 0.18 30 / 20%)",
                color: isPitchError ? "oklch(0.65 0.24 25)" : "oklch(0.75 0.18 30)",
              }}
            >
              {isPitchError ? "PITCH ERROR" : "TIMING ERROR"}
            </span>
          </div>

          {/* Note comparison */}
          {isPitchError && (
            <div className="flex items-center gap-2 text-sm mb-1">
              <span className="font-mono" style={{ color: "oklch(0.50 0.01 270)" }}>
                Expected:
              </span>
              <span
                className="font-mono font-bold px-2 py-0.5 rounded"
                style={{
                  background: "oklch(0.70 0.18 150 / 15%)",
                  color: "oklch(0.70 0.18 150)",
                }}
              >
                {error.expectedNote}
              </span>
              <span style={{ color: "oklch(0.40 0.01 270)" }}>→</span>
              <span className="font-mono" style={{ color: "oklch(0.50 0.01 270)" }}>
                Played:
              </span>
              <span
                className="font-mono font-bold px-2 py-0.5 rounded"
                style={{
                  background: "oklch(0.65 0.24 25 / 15%)",
                  color: "oklch(0.65 0.24 25)",
                }}
              >
                {error.playedNote}
              </span>
            </div>
          )}

          {/* Timing info */}
          {error.timing && (
            <div className="text-xs font-mono" style={{ color: "oklch(0.55 0.01 270)" }}>
              Timing: {error.timing.rating.toUpperCase()}
              {" • "}
              {error.timing.offsetMs > 0 ? "+" : ""}
              {Math.round(error.timing.offsetMs)}ms
              {!error.timing.onTime && ` (${error.timing.offsetMs > 0 ? "late" : "early"})`}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
